import { useEffect, useRef, useState } from 'react';
import LangEnum from '../../../lang/LangEnum';
import { ReauthWarning } from '../../../services/modules/sso/sso.types';
import {
  computeReauthMinutesRemaining,
  resolveReauthWarning,
  startFederatedLogin,
} from '../../../services/modules/sso/sso.utils';
import snackbarStore from '../../../stores/Snackbar.store';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../translation/TranslationContext';
import useSsoReauthDeadline from './useSsoReauthDeadline';

const REAUTH_WARNING_CHECK_INTERVAL_MS = 30_000;

const useSsoReauthWarning = () => {
  const reauthDeadline = useSsoReauthDeadline();
  const { brandSlug } = useApplicationContext();
  const { translate } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(0);
  const lastWarningRef = useRef<ReauthWarning | null>(null);

  useEffect(() => {
    if (!reauthDeadline) {
      return;
    }

    const checkReauthWarning = () => {
      const minutes = computeReauthMinutesRemaining(reauthDeadline, new Date());
      const warning = resolveReauthWarning({
        deadline: reauthDeadline,
        minutesRemaining: minutes,
        lastWarning: lastWarningRef.current,
      });

      setMinutesRemaining(minutes);

      if (!warning) {
        return;
      }

      lastWarningRef.current = warning;

      if (warning.kind === 'modal') {
        setIsModalOpen(true);
        return;
      }

      snackbarStore.handleMessage({
        type: 'warning',
        message: translate(LangEnum.SSO_REAUTH_WARNING_MESSAGE, { minutes }),
      });
    };

    checkReauthWarning();
    const intervalId = setInterval(checkReauthWarning, REAUTH_WARNING_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [reauthDeadline, translate]);

  const closeModal = () => setIsModalOpen(false);

  const handleReauthenticate = () => {
    if (!brandSlug) {
      return;
    }

    startFederatedLogin({
      brandSlug,
      redirectPath: `${window.location.pathname}${window.location.search}`,
    });
  };

  return {
    isModalOpen,
    minutesRemaining,
    closeModal,
    handleReauthenticate,
  };
};

export default useSsoReauthWarning;
